import { axios } from "../axiosInstance";
import type {
  FirmContactCreate,
  FirmContactView,
} from "../interfaces/models/IFirmContact";
import { toFirmContactPayload } from "../utils/firmContactUtils";

export type FirmContactSavePayload = FirmContactCreate & { id?: string };

type FirmContactWriteInput =
  | FirmContactSavePayload
  | FirmContactView
  | (FirmContactView & { firm?: string; office?: string });

/** API may return one document or a list depending on the query. */
export function asFirmContactList(data: unknown): FirmContactView[] {
  if (data == null) {
    return [];
  }
  return Array.isArray(data)
    ? (data as FirmContactView[])
    : [data as FirmContactView];
}

function sanitizeFirmContactPayload(
  payload: FirmContactSavePayload,
): FirmContactSavePayload {
  const sanitized: FirmContactSavePayload = {
    firmId: String(payload.firmId ?? "").trim(),
    officeId: String(payload.officeId ?? "").trim(),
    primaryContactId: String(payload.primaryContactId ?? "").trim(),
    secondaryContactId: String(payload.secondaryContactId ?? "").trim(),
    accountingContactId: String(payload.accountingContactId ?? "").trim(),
    marketingContactIds: (payload.marketingContactIds ?? []).filter(Boolean),
    liaisonContactIds: (payload.liaisonContactIds ?? []).filter(Boolean),
  };

  const id = String(payload.id ?? "").trim();
  if (id) {
    sanitized.id = id;
  }

  const firmName = String(payload.firmName ?? "").trim();
  if (firmName) {
    sanitized.firmName = firmName;
  }

  if (payload.jurisdiction && typeof payload.jurisdiction === "object") {
    sanitized.jurisdiction = payload.jurisdiction;
  }

  for (const key of [
    "itContactId",
    "socialMediaContactId",
    "hrContactId",
    "ccContactId",
  ] as const) {
    const value = String(payload[key] ?? "").trim();
    if (value) {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

function resolveSavePayload(input: FirmContactWriteInput): FirmContactSavePayload {
  if ("primaryContact" in input) {
    const view = input as FirmContactView;
    return sanitizeFirmContactPayload(
      toFirmContactPayload(view, String(view.id ?? "").trim() || undefined),
    );
  }

  const record = input as FirmContactSavePayload & {
    firm?: string;
    office?: string;
  };

  return sanitizeFirmContactPayload({
    ...record,
    firmId: String(record.firmId ?? record.firm ?? "").trim(),
    officeId: String(record.officeId ?? record.office ?? "").trim(),
  });
}

export const getFirmContact = async (): Promise<FirmContactView[]> => {
  try {
    const response = await axios.Get("./firmContact");
    return asFirmContactList(response.data);
  } catch (error) {
    console.error("Error fetching firm contacts:", error);
    return [];
  }
};

export const getFirmContactByFirmId = async (
  firmId: string,
): Promise<FirmContactView[]> => {
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        firmId: { value: firmId.trim(), comparisonType: "EQUAL" },
      }),
    );
    const response = await axios.Get(`./firmContact?${params.toString()}`);
    return asFirmContactList(response.data);
  } catch (error) {
    console.error("Error fetching firm contacts by firm:", error);
    throw error;
  }
};

export const getFirmContactByOfficeId = async (
  officeId: string,
): Promise<FirmContactView | undefined> => {
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        officeId: { value: officeId.trim(), comparisonType: "EQUAL" },
      }),
    );
    const response = await axios.Get(`./firmContact?${params.toString()}`);
    return asFirmContactList(response.data)[0];
  } catch (error) {
    console.error("Error fetching firm contact by office:", error);
    return undefined;
  }
};

export const createFirmContact = async (
  firmContact: FirmContactWriteInput,
): Promise<unknown> => {
  try {
    const { id: _ignored, ...payload } = resolveSavePayload(firmContact);
    const response = await axios.Post("/firmContact", payload);
    return response.data;
  } catch (error) {
    console.error("Error creating firm contact:", error);
    throw error;
  }
};

export const updateFirmContact = async (
  firmContact: FirmContactWriteInput,
): Promise<unknown> => {
  try {
    const payload = resolveSavePayload(firmContact);
    const response = await axios.PutDataIntoModel("/firmContact", payload);
    return response.data;
  } catch (error) {
    console.error("Error updating firm contact:", error);
    throw error;
  }
};

/** POST when no persisted id; PUT when the document id is known. */
export const saveFirmContact = async (
  firmContact: FirmContactWriteInput,
): Promise<unknown> => {
  const payload = resolveSavePayload(firmContact);
  if (String(payload.id ?? "").trim()) {
    return updateFirmContact(payload);
  }
  return createFirmContact(payload);
};

export const deleteFirmContact = async (id: string): Promise<boolean> => {
  try {
    const response = await axios.Delete("/firmContact", `${id}`);
    return response.status === 200;
  } catch (error) {
    console.error("Error deleting firm contact:", error);
    throw error;
  }
};
